import { bbIsRunning } from '/bb/index.js';
import { performBbCollect, bbHoldExtraMs } from './feature.js';
import { getBbIntegrationState, recordBbCollectHoldTimeout } from './integration-state.js';

const BB_FINAL_SUBMIT_ATTR = 'data-rs-final-submit';

let attached = false;
let lastSubmitter = null;
const pendingForms = new WeakSet();
const resumingForms = new WeakSet();

export function attachBbAutoSave() {
  if (attached || typeof document === 'undefined') return;

  document.addEventListener('click', clickHandler, { capture: true });
  document.addEventListener('submit', submitHandler);
  attached = true;
}

export function detachBbAutoSave() {
  if (!attached || typeof document === 'undefined') return;

  document.removeEventListener('click', clickHandler, true);
  document.removeEventListener('submit', submitHandler);
  attached = false;
  lastSubmitter = null;
}

function clickHandler(e) {
  const submitter = resolveSubmitter(e.target);
  if (submitter) lastSubmitter = submitter;
}

function submitHandler(e) {
  const form = e.target;
  if (!isForm(form)) return;

  const state = getBbIntegrationState();
  if (!state.enabled || !state.auto) return;

  if (resumingForms.has(form)) {
    resumingForms.delete(form);
    return;
  }

  const submitter = resolveEventSubmitter(e, form);

  if (pendingForms.has(form)) {
    if (!e.defaultPrevented) stopSubmit(e);
    return;
  }

  if (!hasAttr(submitter, BB_FINAL_SUBMIT_ATTR)) return;
  if (!bbIsRunning()) return;

  const collectPromise = Promise.resolve(performBbCollect());
  const holdMs = state.holdSubmitTimeoutMs > 0 ? state.holdSubmitTimeoutMs + bbHoldExtraMs() : 0;

  if (e.defaultPrevented) {
    trackCollectWithoutResume(form, collectPromise, holdMs);
    return;
  }

  if (holdMs <= 0) return;

  stopSubmit(e);
  pendingForms.add(form);

  waitForCollectOrHoldTimeout(collectPromise, holdMs).then(function () {
    pendingForms.delete(form);
    resumeSubmit(form, submitter);
  });
}

function trackCollectWithoutResume(form, collectPromise, timeoutMs) {
  pendingForms.add(form);

  const wait =
    timeoutMs > 0 ? waitForCollectOrHoldTimeout(collectPromise, timeoutMs) : collectPromise.catch(function () {});
  Promise.resolve(wait).then(function () {
    pendingForms.delete(form);
  });
}

function waitForCollectOrHoldTimeout(collectPromise, timeoutMs) {
  let timer = null;
  const holdTimeout = new Promise(function (resolve) {
    timer = setTimeout(function () {
      recordBbCollectHoldTimeout();
      resolve();
    }, timeoutMs);
  });

  return Promise.race([collectPromise.catch(function () {}), holdTimeout]).then(function () {
    if (timer !== null) clearTimeout(timer);
  });
}

function stopSubmit(e) {
  e.preventDefault();
  e.stopImmediatePropagation();
}

function resumeSubmit(form, submitter) {
  resumingForms.add(form);
  try {
    if (typeof form.requestSubmit === 'function') {
      if (submitter) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    } else {
      form.submit();
    }
  } catch (e) {
    form.submit();
  } finally {
    setTimeout(function () {
      resumingForms.delete(form);
    }, 0);
  }
}

function resolveEventSubmitter(e, form) {
  if (e.submitter) return e.submitter;
  if (lastSubmitter && lastSubmitter.form === form) return lastSubmitter;
  return null;
}

function hasAttr(el, attr) {
  return !!(el && typeof el.hasAttribute === 'function' && el.hasAttribute(attr));
}

function resolveSubmitter(target) {
  if (!target || !target.closest) return null;

  const el = target.closest('button,input');
  if (!el || !el.form) return null;

  const tag = (el.tagName || '').toUpperCase();
  const type = (el.getAttribute('type') || '').toLowerCase();
  if (tag === 'BUTTON') return !type || type === 'submit' ? el : null;
  if (tag === 'INPUT') return type === 'submit' || type === 'image' ? el : null;
  return null;
}

function isForm(el) {
  return el && (el.tagName || '').toUpperCase() === 'FORM';
}
